import { ComponentProps, useRef } from 'react';
import classNames from 'classnames';
import { InputWrap } from '..';
// import { Box } from '@fastlooptech/layout-components';
import { useInputStyle } from '../_common';
import { Box } from '@/layouts/LayoutComponents';
import { InputsNS } from '@/types/inputs';

export type InputTextAreaProps = Omit<ComponentProps<typeof InputWrap>, 'children' | 'htmlFor'> & {
  'data-test-id'?: string;
  /**
   * The id of the input.
   * @type string
   * @required
   */
  id: string;
  /**
   * The aria-label of the input.
   * @type string
   * @optional
   */
  'aria-label'?: string;
  /**
   * The placeholder of the input.
   * @type string
   * @required
   */
  placeholder?: string;
  /**
   * The default value of the input.
   * @type string
   * @optional
   */
  defaultValue?: string;
  /**
   * The name of the input.
   * @type string
   * @required
   */
  name: string;
  /**
   * The boolean that determines if the input is disabled.
   * @type boolean
   * @optional
   * @default false
   */
  disabled?: boolean;
  /**
   * The className of the input.
   * @type string
   * @optional
   */
  className?: string;
  /**
   * The value of the input.
   * @optional
   */
  value?: string;
  /**
   * The on change function.
   * @optional
   */
  onChange?: (event: React.ChangeEvent<HTMLTextAreaElement>) => void;
  /**
   * The on blur function.
   * @optional
   */
  onBlur?: (event: React.ChangeEvent<HTMLTextAreaElement>) => void;
  /**
   * Optional cols of the textarea.
   * @type number | string;
   * @optional
   */
  cols?: number;
  /**
   * Optional rows of the textarea.
   * @type number | string;
   * @optional
   */
  rows?: number;
  /**
   * Optional char length of the textarea.
   * @type number | string;
   * @optional
   */
  maxLength?: number;
};

export function InputTextArea(props: InputTextAreaProps) {
  const inputRef = useRef<HTMLTextAreaElement>(null);

  const inputStyle = useInputStyle(
    props?.assistive?.find((assistive: any) => {
      if (props?.assistive && props?.assistive?.length === 1) return props?.assistive;
      return assistive.type !== InputsNS.StatusType.Informative;
    })?.type
  );

  return (
    <InputWrap
      label={props?.label}
      assistive={props?.assistive}
      required={props?.required}
      width={props?.width}
      htmlFor={props?.id}
      disabled={props?.disabled}
    >
      <textarea
        aria-disabled={props?.disabled}
        className={classNames(
          // Size
          'w-input-width min-h-13',
          // Padding
          'py-input-padding-y px-input-padding-x',
          // Border color
          ' border-input-default-border autofill:border-input-autofilled-border disabled:border-input-disabled-border',
          // Border width
          'border-input border-b-input-b',
          // Border radius
          'rounded-input',
          // Text color
          'text-input-default-text disabled:text-input-disabled-text autofill:text-input-autofilled-text',
          // Background color
          'bg-input-default-bg disabled:bg-input-disabled-bg autofill:bg-input-autofilled-bg',
          // Placeholder color
          'placeholder:text-input-default-placeholder disabled:placeholder:text-input-disabled-placeholder',
          // Text size`
          'text-input',
          // Font family
          'font-input placeholder:font-input',
          // Others
          'resize-none disabled:cursor-not-allowed outline-none text-ellipsis transition-shadow disabled:hover:shadow-none',
          inputStyle.border,
          inputStyle.focusBorder,
          props?.className,
        )}
        id={props?.id}
        ref={inputRef}
        data-test-id={props['data-test-id']}
        aria-label={props['aria-label']}
        placeholder={props?.placeholder}
        defaultValue={props?.defaultValue}
        name={props?.name}
        disabled={props?.disabled}
        onChange={props?.onChange}
        onBlur={props?.onBlur}
        value={props?.value}
        required={props?.required}
        autoComplete={'off'}
        cols={props?.cols}
        rows={props?.rows}
        autoFocus={false}
        maxLength={props?.maxLength || 255}
      />
    </InputWrap>
  );
}
